import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";

import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";

import KMKDateRangePicker from "../../../Common/KMKDateRangePicker";
import TableWithPaginationSorting from "../../../Common/Table/TableWithPaginationSorting";
import { KMKDialog } from "../../../Common";

import {
  Job_Status_Types,
  rowsPerPageOptions,
  PipelineHistoryColumns,
  Detailed_Pipeline_Columns,
} from "../utils/JobData";
import { convertTimezone } from "../../../../util/common";
import { dayjs } from "../../../../util/dayjs";
import DataPipelineService from "../../../../service/dataPipelineService";
import JobHistoryPopup from "./JobHistory";

const useStyles = makeStyles()(() => ({
  dialogPaper: {
    "& .MuiDialog-paper": {
      maxWidth: "960px",
      width: "960px",
    },
  },
  contentContainer: {
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
    padding: "12px 24px 24px",
    borderRadius: "24px",
    backgroundColor: "#FFFFFF",
  },
}));

const formatDate = (date, displayFormat) =>
  displayFormat ? date.format("MM/DD/YYYY HH:mm:ss") : date.format("YYYY-MM-DD HH:mm:ss");

export default function PipelineHistory({ handleCancel, DataPipeline_id, PipelineName }) {
  const { Workspace_id } = useParams();

  const workspace = useSelector((state) => state.workspace);
  const { IsDataVaultEnabled } = workspace.workspace;

  const user = useSelector((state) => state.user);
  const { UserTimezone } = useMemo(() => user?.currentUser || {}, [user]);

  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [needDetails, setNeedDetails] = useState(false);
  const [detailedHistory, setDetailedHistory] = useState([]);

  const [chain_id, setChain_id] = useState(-1);
  const [dateRange, setDateRange] = useState(null);
  const [displayDate, setDisplayDate] = useState(null);
  //variables useful for pagination and sorting
  const [count, setCount] = useState(0);
  const [detailCount, setDetailCount] = useState(0);
  const [options, setOptions] = useState({
    orderBy: "StartTime",
    order: "desc",
  });
  const [detailOptions, setDetailOptions] = useState({
    limit: 50,
    page: 0,
    orderBy: "StartTime",
    order: "desc",
  });
  const [historyPopup, setHistoryPopup] = useState({
    open: false,
    Job_id: null,
    JobType: null,
    JobName: "",
  });

  const optionsRef = useRef(options);
  const detailedOptionsRef = useRef(detailOptions);
  const { classes } = useStyles();

  const disabledDate = useCallback((current) => current && current > dayjs().endOf("day"), []);

  const handleBackClick = () => {
    setDateRange(null);
    setDetailOptions({ limit: 50, page: 0, orderBy: "StartTime", order: "desc" });
    setDetailedHistory([]);
    setDetailCount(0);

    setNeedDetails(false);
  };

  // Get History List
  const getHistoryList = useCallback(
    async (dates, tableOptions = optionsRef.current) => {
      setLoading(true);

      const datapipeline = new DataPipelineService();
      const result = await datapipeline.getPipelineHistory(
        Workspace_id,
        DataPipeline_id,
        dates && dates[0] ? formatDate(dates[0]) : undefined,
        dates && dates[1] ? formatDate(dates[1]) : undefined,
        { ...tableOptions, UserTimezone }
      );

      if (result.success) {
        const { historyData } = result.data;
        let historyDataArr = [];

        historyData.length &&
          historyData.forEach((row) => {
            historyDataArr.push([
              {
                Chain_id: row.Chain_id,
                value: row.Name,
                align: "left",
              },
              {
                Chain_id: row.Chain_id,
                value: Job_Status_Types[row.Status?.toLowerCase()]?.statusText ?? "-",
                align: "left",
                color: Job_Status_Types[row.Status?.toLowerCase()]?.color,
              },
              {
                Chain_id: row.Chain_id,
                value: row.StartTime ? convertTimezone(row.StartTime) : "-",
                align: "left",
              },
              {
                Chain_id: row.Chain_id,
                value: row.EndTime ? convertTimezone(row.EndTime) : "-",
                align: "left",
              },
              {
                Chain_id: row.Chain_id,
                value: row.Jobs,
                align: "left",
              },
              {
                Chain_id: row.Chain_id,
                value: row.RunBy,
                align: "left",
              },
            ]);
          });
        setHistoryList(historyDataArr);
      }
      setDataLoaded(true);
      setLoading(false);
    },
    [Workspace_id, DataPipeline_id, UserTimezone]
  );

  // Get Detailed History List
  const getDetailedHistoryList = useCallback(
    async (dates, tableOptions = detailedOptionsRef.current, Chain_id) => {
      setLoading(true);

      const datapipeline = new DataPipelineService();
      const result = await datapipeline.getJobsHistory(
        Workspace_id,
        DataPipeline_id,
        Chain_id,
        dates && dates[0] ? formatDate(dates[0]) : undefined,
        dates && dates[1] ? formatDate(dates[1]) : undefined,
        tableOptions
      );
      if (result.success) {
        const { historyData, count } = result.data;
        let historyDataArr = [];

        historyData.forEach((row) => {
          historyDataArr.push([
            { Job_id: row.Job_id, Type: row.Type, value: row.Name, align: "left" },
            {
              Job_id: row.Job_id,
              value: Job_Status_Types[row.Status?.toLowerCase()]?.statusText ?? "-",
              align: "left",
              color: Job_Status_Types[row.Status?.toLowerCase()]?.color,
            },
            {
              Job_id: row.Job_id,
              value: row.StartTime ? convertTimezone(row.StartTime) : "-",
              align: "left",
            },
            {
              Job_id: row.Job_id,
              value: row.EndTime ? convertTimezone(row.EndTime) : "-",
              align: "left",
            },
            {
              Job_id: row.Job_id,
              value: row.message,
              align: "left",
              color:
                row.Status?.toLowerCase() === "failed" || row.Status?.toLowerCase() === "error"
                  ? Job_Status_Types[row.Status?.toLowerCase()]?.color
                  : "black",
            },
          ]);
        });
        setDetailedHistory(historyDataArr);
        setDetailCount(count);
      } else setDetailCount(0);

      setDataLoaded(true);
      setLoading(false);
    },
    [Workspace_id, DataPipeline_id]
  );

  const getDataUsingOptions = (tableOptions) => {
    getHistoryList(dateRange, tableOptions);
  };

  const getDetailedDataUsingOptions = (tableOptions) => {
    getDetailedHistoryList(dateRange, tableOptions, chain_id);
  };

  const getDetailedHistory = (row) => {
    let StartTime = row[0].value !== "-" ? new Date(row[0].value) : null;
    let EndTime = row[1].value !== "-" ? new Date(row[1].value) : null;

    setDisplayDate([dayjs(StartTime), dayjs(EndTime)]);
    if (!IsDataVaultEnabled) {
      setDateRange([dayjs(StartTime), dayjs(EndTime)]);
      getDetailedHistoryList([StartTime, EndTime], detailOptions, row[0]?.Chain_id);
    } else getDetailedHistoryList(dateRange, detailOptions, row[0]?.Chain_id);

    setChain_id(row[0]?.Chain_id);
    setNeedDetails(true);
  };

  useEffect(() => {
    getHistoryList(null);

    return () => {
      setLoading(false);
      setHistoryList([]);
      setDataLoaded(false);
      setDateRange(null);
      setCount(0);
      setOptions({ limit: 10, page: 0, orderBy: "StartTime", order: "desc" });
      setDetailOptions({ limit: 50, page: 0, orderBy: "StartTime", order: "desc" });
    };
  }, [getHistoryList]);

  const getJobsHistory = (row) => {
    const { Job_id, Type, value: Name } = row[0];

    setHistoryPopup({
      open: true,
      Job_id,
      JobType: Type,
      JobName: Name,
    });
  };

  if (historyPopup.open)
    return (
      <JobHistoryPopup
        handleCancel={() => setHistoryPopup((prev) => ({ ...prev, open: false }))}
        popupTitle={`"${historyPopup.JobName}" History`}
        jobId={historyPopup.Job_id}
        jobType={historyPopup.JobType}
        fromPipeline
      />
    );

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !dataLoaded}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} open={dataLoaded} onClose={handleCancel}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <DialogTitle sx={{ display: "flex", padding: "16px", marginBottom: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {needDetails ? (
              <ArrowBackIcon onClick={() => handleBackClick()} sx={{ marginRight: "10px", cursor: "pointer" }} />
            ) : (
              <HistoryIcon sx={{ marginRight: "10px" }} />
            )}
            <span>
              {needDetails &&
                displayDate &&
                !IsDataVaultEnabled &&
                `: "${formatDate(displayDate[0], true)}" - "${
                  displayDate[1] === null ? "Current" : formatDate(displayDate[1], true)
                }"`}
              {PipelineName} History
            </span>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              color: "black",
              marginLeft: "auto",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.contentContainer}>
          <Grid container columnSpacing={2} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
            {!needDetails && (
              <Grid alignSelf="center" size={5}>
                <KMKDateRangePicker
                  format={(value) => value.format("MM/DD/YYYY HH:mm:ss")}
                  value={dateRange}
                  onChange={(value) => {
                    setOptions({ ...options, page: 0 });
                    setDateRange(value);
                    getHistoryList(value, { ...options, page: 0 }, false);
                  }}
                  disabledDate={disabledDate}
                  size="middle"
                  separator="~"
                  allowClear={{ clearIcon: <ClearIcon sx={{ fontSize: "16px" }} /> }}
                  suffixIcon={
                    dateRange ? <ClearIcon sx={{ fontSize: "16px" }} /> : <DateRangeIcon sx={{ fontSize: "16px" }} />
                  }
                  variant="outlined"
                  showTime
                />
              </Grid>
            )}
          </Grid>
          <Box sx={{ marginTop: "20px" }}>
            {!needDetails && (
              <TableWithPaginationSorting
                columns={PipelineHistoryColumns}
                tableData={historyList}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={5}
                noDataMessage="No records found"
                handleRequest={getDataUsingOptions}
                rowsPerPageOptions={rowsPerPageOptions}
                defaultOrder="desc"
                defaultOrderBy="StartTime"
                options={options}
                setOptions={setOptions}
                count={count}
                RowClick={getDetailedHistory}
                rowHover={true}
                usePagination={false}
                showCopy={true}
              />
            )}
            {needDetails && (
              <TableWithPaginationSorting
                columns={Detailed_Pipeline_Columns}
                tableData={detailedHistory}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={5}
                noDataMessage="No records found"
                handleRequest={getDetailedDataUsingOptions}
                rowsPerPageOptions={rowsPerPageOptions}
                defaultOrder="desc"
                defaultOrderBy="StartTime"
                options={detailOptions}
                setOptions={setDetailOptions}
                count={detailCount}
                showCopy={rowsPerPageOptions}
                RowClick={getJobsHistory}
                rowHover={true}
              />
            )}
          </Box>
        </DialogContent>
      </KMKDialog>
    </>
  );
}

PipelineHistory.propTypes = {
  DataPipeline_id: PropTypes.number,
  handleCancel: PropTypes.func,
};
